import { fetchBillOfMaterials } from '$lib/api/bill_of_materials';
import { fetchElectricalLayout } from '$lib/api/electrical_layout';
import { fetchFolderZip, getDownloadURL } from '$lib/api/project_files';
import { fetchFirmwareMappings } from '$lib/api/rcu_mappings';
import { generateElectricalLayoutPdf } from '$lib/utils/electricalLayoutPdf';
import { generateUnitLogicSheetPdf } from '$lib/utils/logicSheetPdf';
import { buildExportData } from '$lib/utils/electricalLayoutData';
import type { UnitDomainGraphResponse } from '$lib/types/domain_graph';

function downloadJSON(data: unknown, filename: string) {
	const blob = new Blob([JSON.stringify(data, null, 2)], {
		type: 'application/json'
	});

	const url = URL.createObjectURL(blob);

	const a = document.createElement('a');
	a.href = url;
	a.download = filename;
	a.click();

	URL.revokeObjectURL(url);
}

export async function downloadElectricalLayout(
	unitId: number,
	filename: string,
	domainGraph: UnitDomainGraphResponse
) {
	const electricalSummary = await fetchElectricalLayout(unitId);
	const data = { ...buildExportData(domainGraph), electricalSummary };
	await generateElectricalLayoutPdf(data, `${filename}-electrical-layout.pdf`);
}

function downloadBlob(blob: Blob, filename: string) {
	const url = URL.createObjectURL(blob);
	const a = document.createElement('a');
	a.href = url;
	a.download = filename;
	a.click();
	URL.revokeObjectURL(url);
}

export async function downloadRCUMappings(
	unitId: number,
	exportName: string,
	exportType: string = 'v3'
) {
	const response = await fetchFirmwareMappings(unitId, exportType);

	if (exportType === 'v3') {
		const data = await response.json();
		downloadJSON(data, `${exportName}-rcu-mappings.json`);
	} else if (exportType === 'v2_json') {
		const text = await response.text();
		const blob = new Blob([text], { type: 'text/plain' });
		downloadBlob(blob, `${exportName}-rcu-mappings.txt`);
	} else if (exportType === 'v2_header') {
		const text = await response.text();
		const blob = new Blob([text], { type: 'text/plain' });
		downloadBlob(blob, `${exportName}-rcu-mappings.h`);
	} else if (exportType === 'all') {
		const blob = await response.blob();
		downloadBlob(blob, `${exportName}-rcu-mappings.zip`);
	}
}

export async function downloadBillOfMaterials(projectId: number, exportName: string) {
	const date = new Date();
	const year = date.getFullYear();
	const month = String(date.getMonth() + 1).padStart(2, '0');
	const day = String(date.getDate()).padStart(2, '0');
	const formattedDate = `${day}-${month}-${year}`;
	const blob = await fetchBillOfMaterials(projectId);

	const url = URL.createObjectURL(blob);
	const a = document.createElement('a');
	a.href = url;
	a.download = `${exportName}-bill-of-materials-${formattedDate}.xlsx`;
	a.click();
	URL.revokeObjectURL(url);
}

export async function downloadProjectFile(fileId: number) {
	const response = await getDownloadURL(fileId);
	const downloadUrl = response.download_url;
	const a = document.createElement('a');
	a.href = downloadUrl;
	a.download = '';
	a.style.display = 'none';

	document.body.appendChild(a);
	a.click();
	a.remove();
}

export async function downloadFolderZip(
	projectId: number,
	folderId: number,
	folderName: string,
	unitId?: number
) {
	const blob = await fetchFolderZip(projectId, folderId, unitId);
	downloadBlob(blob, `${folderName}.zip`);
}

export async function downloadLogicSheet(
	exportName: string,
	domainGraph: UnitDomainGraphResponse
) {
	const base = buildExportData(domainGraph);
	const loads = base.rooms.flatMap((r) => r.loads.map((load) => ({ ...load, room: r.id })));
	const keyActions = base.keypadKeys.flatMap((input) =>
		(input.actions || []).map((action) => ({ ...action, key: input.id }))
	);

	const savedLedMappings = (domainGraph.layout.led_scenes ?? []).map((scene: any) => ({
		x_address: scene.trigger_address,
		x_key: scene.trigger_key,
		x_state: scene.trigger_event_type === 'on' ? 1 : 0,
		led_targets: (scene.led_loads ?? []).map((load: any) => ({
			address: load.address,
			key: load.key_index,
			state: load.state
		}))
	}));

	const data = {
		...base,
		loads,
		keyActions,
		savedLedMappings
	};

	await generateUnitLogicSheetPdf(data, `${exportName}-logic_sheet.pdf`);
}

export async function downloadLoadChart(
	unitId: number,
	exportName: string,
	domainGraph: UnitDomainGraphResponse
) {
	const base = buildExportData(domainGraph);
	const loads = base.rooms.flatMap((r) => r.loads.map((load) => ({ ...load, room: r.id })));
	const keyActions = base.keypadKeys.flatMap((input) =>
		(input.actions || []).map((action) => ({ ...action, key: input.id }))
	);

	const data = {
		...base,
		loads,
		keyActions,
		savedLedMappings: []
	};

	await generateUnitLoadChartPdf(data, `${exportName}-load_chart.pdf`);
}
